import { useState, useEffect, useMemo } from 'react';

interface ResponsiveState {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  width: number;
  height: number;
}

// Tailwind CSS breakpoints for consistency
const BREAKPOINTS = {
  mobile: 768, // Below md
  tablet: 1024, // md to lg
  desktop: 1024, // lg and above
};

export const useResponsive = (): ResponsiveState => {
  const [state, setState] = useState<ResponsiveState>(() => {
    // Server-side rendering safe initial state
    if (typeof window === 'undefined') {
      return {
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        width: 1024,
        height: 768,
      };
    }

    const width = window.innerWidth;
    const height = window.innerHeight;

    return {
      isMobile: width < BREAKPOINTS.mobile,
      isTablet: width >= BREAKPOINTS.mobile && width < BREAKPOINTS.desktop,
      isDesktop: width >= BREAKPOINTS.desktop,
      width,
      height,
    };
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      setState({
        isMobile: width < BREAKPOINTS.mobile,
        isTablet: width >= BREAKPOINTS.mobile && width < BREAKPOINTS.desktop,
        isDesktop: width >= BREAKPOINTS.desktop,
        width,
        height,
      });
    };

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Call handler right away so state gets updated with initial window size
    handleResize();

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return state;
};

// Additional utility hooks for common use cases
export const useIsMobile = (): boolean => {
  const { isMobile } = useResponsive();
  return isMobile;
};

export const useIsDesktop = (): boolean => {
  const { isDesktop } = useResponsive();
  return isDesktop;
};

// Hook for touch device detection (more accurate than just screen size)
export const useIsTouchDevice = (): boolean => {
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    const checkTouchDevice = () => {
      return (
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        // @ts-ignore - older browsers
        navigator.msMaxTouchPoints > 0
      );
    };

    setIsTouchDevice(checkTouchDevice());
  }, []);

  return isTouchDevice;
};

// Hook that combines screen size and touch detection for optimal UX decisions
// Memoized to prevent creating new object references on every render
export const useMobileFirst = (): {
  showMobileUI: boolean;
  showDesktopUI: boolean;
  isTouchDevice: boolean;
  screenSize: 'mobile' | 'tablet' | 'desktop';
} => {
  const { isMobile, isTablet, isDesktop } = useResponsive();
  const isTouchDevice = useIsTouchDevice();

  return useMemo(() => ({
    showMobileUI: isMobile || (isTablet && isTouchDevice),
    showDesktopUI: isDesktop && !isTouchDevice,
    isTouchDevice,
    screenSize: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop',
  }), [isMobile, isTablet, isDesktop, isTouchDevice]);
};